'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { cn, spacingClasses } from "@/lib/utils";
import React from "react";
import { sectionVariants } from "@/types";
import { sectionBackgroundColors } from "@/lib/utils";
import { PageContext } from '@/components/builder';

interface LevelProps {
  override?: boolean;
  customLabel?: string;
  customHref?: string;
}

interface CustomBreadcrumbProps {
  darkMode?: boolean;
  variant?: sectionVariants;
  level1?: LevelProps;
  level2?: LevelProps;
  level3?: LevelProps;
  level4?: LevelProps;
  attributes?: React.HTMLProps<HTMLElement>;
  className?: string;
}

export const CustomBreadcrumb = ({
  darkMode = false,
  variant = "A",
  level1 = {},
  level2 = {},
  level3 = {},
  level4 = {},
  attributes,
  className,
}: CustomBreadcrumbProps) => {
  const pathname = usePathname();
  const segments = (pathname || '').split('/').filter(Boolean);
  const maxLevels = 4;
  const pageFields = React.useContext(PageContext);

  const displaySegments =
  segments.length <= maxLevels
    ? segments
    : [segments[0], segments[1], segments[segments.length - 1]
  ].filter(Boolean).slice(0, maxLevels - 1);

  const { key, ...rest } = attributes ?? {};
  const overrides = [level1, level2, level3, level4];
  // const reversedOverrides = [...overrides].reverse();

  const breadcrumbClass = cn(
    spacingClasses["small"],
    darkMode ? 'dark' : 'light',
    'paragraph-sm capitalize',
    sectionBackgroundColors(darkMode, variant),
    'text-foreground',
    className,
  );

  const lastAvailableOverrideIndex = overrides.findIndex(override => override?.override && override?.customLabel);
  const fallbackSegmentIndex = segments.length > 1 ? Math.max(0, segments.length) - 1 : 0;

  const mobileText = lastAvailableOverrideIndex !== -1 && overrides[fallbackSegmentIndex]?.override 
    ? (overrides[fallbackSegmentIndex]?.customLabel ?? '').replace(/-/g, ' ')
    : decodeURIComponent((segments[fallbackSegmentIndex - 1] ?? '').replace(/-/g, ' '));

  const mobileLink = '/' + segments.slice(0, -1).join('/');

  return (
    <section
      key={key}
      {...rest}
      className={breadcrumbClass}
    >
      <Breadcrumb className="hidden lg:block container">
        <BreadcrumbList>
          <BreadcrumbItem>
            {level1?.override ? (
              <BreadcrumbLink asChild>
                <Link href={level1.customHref ?? '/'}>{level1.customLabel ?? 'Home'}</Link>
              </BreadcrumbLink>
            ) : (
              <BreadcrumbLink asChild>
                <Link href="/">Home</Link>
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
          {displaySegments.length > 0 && <BreadcrumbSeparator>/</BreadcrumbSeparator>}

          {displaySegments.map((segment, i) => {
            const isLast = i === displaySegments.length - 1;
            const href = '/' + segments.slice(0, i + 1).join('/');
            const defaultLabel = decodeURIComponent(segment.replace(/-/g, ' '));
            const override = overrides[i + 1]; // level2 and level3

            const label = override?.override ? override.customLabel ?? defaultLabel : defaultLabel;
            const link = override?.override ? override.customHref ?? href : href;

            return (
              <React.Fragment key={i}>
                <BreadcrumbItem key={i}>
                  {isLast ? (
                    <BreadcrumbPage>{
                      i > 2 
                        ? label 
                        : (pageFields.data as unknown as { builderModelName?: string })?.builderModelName === "business-guides" || (pageFields.data as unknown as { builderModelName?: string })?.builderModelName === "case-study" || (pageFields.data as unknown as { builderModelName?: string })?.builderModelName === "market-insights"
                          ? "Article" 
                          : label
                    }</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link href={link}>{label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator key={`separator-${i}`}>/</BreadcrumbSeparator>}
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      {mobileText && (
        <Breadcrumb className="block lg:hidden container-full">
          <BreadcrumbList>
            <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link href={mobileLink} className="flex items-center gap-1 text-primary">
                <span className="material-symbols-outlined w-4.5 h-4.5"  style={{ fontSize: '18px' }}>arrow_back</span>
                {mobileText}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      )}
    </section>
  );
}
